import { createContext, ReactNode, useContext } from "react";
import { notification } from "antd";

type NotificationType = "success" | "info" | "warning" | "error";

interface NotificationContextType {
  notify: (options: {
    type: NotificationType;
    title: string;
    description?: string;
  }) => void;
  contextHolder: ReactNode;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

let globalNotify: NotificationContextType["notify"];

const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [api, contextHolder] = notification.useNotification();

  const notify = ({
    type,
    title,
    description,
  }: {
    type: NotificationType;
    title: string;
    description?: string;
  }) => {
    api[type]({
      message: title,
      description: (
        <div dangerouslySetInnerHTML={{ __html: description ?? "" }} />
      ),
    });
  };

  globalNotify = notify;

  return (
    <NotificationContext.Provider value={{ notify, contextHolder }}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const getGlobalNotify = () => {
  if (!globalNotify) {
    throw new Error("NotificationProvider is not initialized");
  }
  return globalNotify;
};

export default NotificationProvider;
